import { useCallback, useState } from "react";
import { z } from "zod";
import {
  Box,
  Icon,
  LIST_ROW_PX,
  Skeleton,
  SkeletonGroup,
  Text,
  WidgetBandHeader,
  WidgetEmpty,
  type WidgetModule,
  type WidgetRenderContext,
} from "@salestrak/ui";

import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import { useIssueView } from "#app/components/issue-view";
import { AGE_STATUS, AGE_STATUS_CONFIG } from "#app/lib/age-status";
import { useBandFolds } from "../shared/use-band-folds";
import { FollowUpListRow } from "./follow-up-list-row";
import {
  FOLLOW_UP_TIERS,
  useFollowUpList,
  type FollowUpBand,
  type FollowUpQueueRow,
  type FollowUpTier,
} from "./use-follow-up-list";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";

/**
 * `follow-up-list` — My Work's "Follow ups" card: every Project follow up the
 * viewer owns, in one queue.
 *
 * Three bands on the one deadline vocabulary, most owed first. Stale and Update
 * soon stand open; while something is owed, On track opens FOLDED — one line
 * with its count — so the card reads "3 · 2 · 6" before a row is read and
 * the eye lands on what is owed. With nothing owed, On track opens: it is
 * the card's content then, and the two muted zero bands above it already say
 * nothing is due. A band that counts zero stands muted, so the structure
 * never comes and goes with the counts; a viewer with no follow up at all
 * gets the kit's empty state. A fold the reader makes is remembered per
 * card, per browser (`useBandFolds`), so a reload finds the bands as they
 * were left; the untouched ones keep following the defaults above.
 *
 * Tier and deadline are derived live on the client from the last Update and
 * the effective cycle — the same derivation the chip on each row makes — so
 * the band a row sits under and the hue it wears can never disagree (the
 * stored status moves on an hourly cron pass). Inside a band the nearest
 * deadline leads.
 *
 * Rows are the kit's `WidgetListRow` ({@link FollowUpListRow}): the age count
 * as a fixed leading gutter — the one follow up fact, tier in its tint,
 * deadline in its tooltip, no glyph (the band already names the tier) — then
 * the Project's chip and name. Hover reveals Update, Open in new tab and Copy
 * link at the row's end. The Update opens Post update, and a posted row
 * leaves its band as the query answers. The row press opens the Project.
 *
 * A **managed-only** card (the My Work catalog) — no `catalog` entry, so it
 * never appears in the free Add-card gallery; a dashboard's team-scoped
 * queue is the `entity-list` "Follow ups due" preset.
 */
export const followUpListConfigSchema = z.looseObject({});

export type FollowUpListConfig = z.infer<typeof followUpListConfigSchema>;

/**
 * Which bands open before the reader touches one. While something is owed,
 * On track is present but quiet — folded to its count — so the card opens
 * on the owed rows. With nothing owed, On track IS the card's content and
 * opens; the two zero bands above it, muted, already say nothing is due, so
 * no sentence repeats it. A reader's own fold or unfold wins over either
 * default and is remembered.
 */
function bandFoldsByDefault(tier: FollowUpTier, owed: number): boolean {
  return tier === AGE_STATUS.onTrack && owed > 0;
}

function FollowUpListRender(_: WidgetRenderContext<FollowUpListConfig>) {
  const { bands, ageSizers, owed, loading } = useFollowUpList();
  const { folded, toggle } = useBandFolds();
  const isExpanded = useCallback(
    (tier: FollowUpTier) => !folded(tier, bandFoldsByDefault(tier, owed)),
    [folded, owed],
  );
  const toggleBand = useCallback(
    (tier: FollowUpTier) => toggle(tier, bandFoldsByDefault(tier, owed)),
    [toggle, owed],
  );

  const { openIssue } = useIssueView();
  const openRow = useCallback(
    (row: FollowUpQueueRow) => {
      openIssue(row.issue, { source: "widget" });
    },
    [openIssue],
  );

  // The verb: one row's Post update dialog at a time.
  const [updateRow, setUpdateRow] = useState<FollowUpQueueRow | null>(null);

  const total = bands.reduce((sum, band) => sum + band.rows.length, 0);
  let body: React.ReactNode;
  if (loading && total === 0) {
    body = <FollowUpListSkeleton />;
  } else if (total === 0) {
    // No follow up at all is not a queue at zero — three muted lines over
    // empty air read as a card that failed to load. It is the kit's empty
    // state, the one Assigned to me wears beside it.
    body = <WidgetEmpty title="No Follow ups" />;
  } else {
    // Headings and rows stand on the one list rung (`LIST_ROW_PX`), so a
    // band and the rows under it run at a single beat. Air only where rows
    // meet the next heading: a band line sits 12px under the last row of the
    // band before it, while two zero lines stack at the rung's own pitch — a
    // run of empty headings spaced like content read as a sparse list, not
    // as structure at rest.
    body = (
      <Box direction="column">
        {bands.map((band, index) => {
          const previous = bands[index - 1];
          const afterRows = previous !== undefined && previous.rows.length > 0 && isExpanded(previous.tier);
          return (
            <FollowUpBandSection
              key={band.tier}
              band={band}
              expanded={isExpanded(band.tier)}
              onToggle={() => toggleBand(band.tier)}
              onOpen={openRow}
              onUpdate={setUpdateRow}
              sizers={ageSizers}
              spaced={afterRows}
            />
          );
        })}
      </Box>
    );
  }

  // Grows to the card body so an empty state centers in it, not at its top
  // — `grow shrink={false}`, never `fill="flex"`: every growing Box value
  // carries `min-h-0`, and inside the card frame's fixed-height column a
  // shrinkable root is clamped to the viewport, the rows spill past it and
  // past the scroll-end gutter seated after it, and the travel ends on the
  // last row with no slack at all. Unshrinkable, the root is as tall as its
  // rows and the gutter lands under them.
  return (
    <Box direction="column" grow shrink={false}>
      {body}
      {updateRow ? (
        <UpdateAgeDialog
          via="follow_up_widget"
          age={updateRow.issue.age}
          issueId={updateRow.id}
          onOpenChange={(next) => {
            if (!next) setUpdateRow(null);
          }}
          open
          stageId={updateRow.issue.stageId}
        />
      ) : null}
    </Box>
  );
}

/**
 * The card's first paint: the three band lines it will stand whatever the
 * answer — their labels are static copy, so they render REAL, with a bar
 * where each count lands — and three anonymous row bars under On track, the
 * band that opens on the common morning (nothing owed) — each a count bar
 * in the age gutter, an icon square, a name bar. Every line is the
 * list rung, as the settled card's are. The whole scaffold
 * fades in on the kit's delayed gate, so a warm answer never flashes it.
 */
function FollowUpListSkeleton() {
  return (
    <SkeletonGroup aria-hidden data-slot="follow-up-list-skeleton">
      <Box direction="column">
        {FOLLOW_UP_TIERS.map((tier) => (
          <Box key={tier} align="center" direction="row" gap="xs" height={LIST_ROW_PX}>
            <Icon color="muted" name="caret-down" size="sm" />
            <Text size="sm" variant="muted" weight="semibold">
              {AGE_STATUS_CONFIG[tier].label}
            </Text>
            <Skeleton height={10} width={12} />
          </Box>
        ))}
        <Box direction="column" paddingLeft="lg">
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
            <Box key={index} align="center" direction="row" gap="sm" height={LIST_ROW_PX}>
              <Box align="center" direction="row" justify="end" shrink={false} width={SKELETON_GUTTER_PX}>
                <Skeleton height={12} width={22} />
              </Box>
              <Skeleton height={16} width={16} />
              <Skeleton height={14} vary={index} width={180} />
            </Box>
          ))}
        </Box>
      </Box>
    </SkeletonGroup>
  );
}

/** Name bars in the row rhythm while the queue loads. */
const SKELETON_ROW_COUNT = 3;

/** The age gutter the settled row sizes from its data — unknowable before
 *  the answer, so the scaffold stands on the common width, a two-digit day
 *  count ("20d" at the `xs` size). */
const SKELETON_GUTTER_PX = 22;

/** One band: the kit's disclosure line, then its rows while open, inset
 *  under the heading's label the way a stack section insets its content
 *  under its caret (`lg`) — the indent is what says the rows belong to the
 *  line above them. A zero band is the muted line alone — nothing would
 *  answer under it. `spaced` adds the 12px lead a heading takes after
 *  another band's rows. */
function FollowUpBandSection({
  band,
  expanded,
  onToggle,
  onOpen,
  onUpdate,
  sizers,
  spaced,
}: {
  band: FollowUpBand;
  expanded: boolean;
  onToggle: () => void;
  onOpen: (row: FollowUpQueueRow) => void;
  onUpdate: (row: FollowUpQueueRow) => void;
  sizers: readonly string[];
  spaced: boolean;
}) {
  const zero = band.rows.length === 0;
  return (
    <Box direction="column" paddingTop={spaced ? "base" : undefined}>
      <WidgetBandHeader
        count={band.rows.length}
        expanded={expanded}
        label={band.label}
        muted={zero}
        onToggle={onToggle}
      />
      {expanded && !zero ? (
        <Box direction="column" paddingLeft="lg">
          {band.rows.map((row) => (
            <FollowUpListRow
              key={row.id}
              onOpen={() => onOpen(row)}
              onUpdate={() => onUpdate(row)}
              row={row}
              sizers={sizers}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

/** The title wears the whole queue — On track included — so the count says
 *  how many follow ups the viewer holds, and the bands say which are owed. */
function FollowUpListTitleCount(_: WidgetRenderContext<FollowUpListConfig>) {
  const { total, loading } = useFollowUpList();
  if (loading && total === 0) return <Skeleton height={10} width={28} />;
  return (
    <Text size="xs" weight="medium" variant="muted">
      · {total.toLocaleString()}
    </Text>
  );
}

export const followUpListDef: WidgetModule<FollowUpListConfig> = {
  type: "follow-up-list",
  title: "Follow ups",
  icon: PROPERTY_ICON.followUp,
  // The rows are the viewer's own Project follow ups, not a slice of the
  // scoped projects — the host's default dataset would be unrelated, so the
  // Data tab hides and the maximized view is this body at dialog size.
  drilldownDataset: null,
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 6, defaultH: 5 },
  configSchema: followUpListConfigSchema,
  titleAdornment: FollowUpListTitleCount,
  render: FollowUpListRender,
};
